import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import sidebarMap from "../../config/menuMapping";
import { useProfile } from "../../context/contextProfile";
import { useDesign } from "../../context/contextDesign";
import { useNav } from "../../context/contextNav";
import { useThemeMode } from "../../context/contextTheme";

const SidebarItems = ({height}) => {
  const { pathname } = useLocation();
  const { theme } = useThemeMode();
  const { sidebarWidthVal, sidebarFontSize } = useDesign();
  const { sidebarExtended } = useProfile();

  const {
    resetSubNav,
    activeNav,
    setActiveNav,
    activeCloudSubNav,
    setActiveCloudSubNav,
    activeComplianceSubNav,
    setActiveComplianceSubNav,
    openCloudSubMenu,
    setOpenCloudSubMenu,
    openComplianceSubMenu,
    setOpenComplianceSubMenu,
    hasCloudAssets,
    hasComplianceAssets,
  } = useNav();

  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const cloudSubNav = Object.values(sidebarMap[3]?.subMenu || {}).find(
      (sub) =>
        pathname === sub.path || pathname.startsWith(`${sub.path}/`)
    );

    if (!cloudSubNav) return;

    setActiveNav(3);
    setActiveCloudSubNav(cloudSubNav.id);
    setActiveComplianceSubNav(null);
    setOpenCloudSubMenu(true);
    setOpenComplianceSubMenu(false);
  }, [
    pathname,
    setActiveNav,
    setActiveCloudSubNav,
    setActiveComplianceSubNav,
    setOpenCloudSubMenu,
    setOpenComplianceSubMenu,
  ]);

  const handleMouseEnter = (e, itemId) => {
    if (!sidebarExtended && (itemId === 3 || itemId === 7)) {
      const rect = e.currentTarget.getBoundingClientRect();
      setHoverPosition({
        top: rect.top - 0,
        left: rect.right + 0,
      });
      setHoveredItem(itemId);
    }
  };

  // 🔹 Collapse submenus when sidebar collapses
  useEffect(() => {
    if (sidebarExtended) {
      setOpenCloudSubMenu(false);
      setOpenComplianceSubMenu(false);
      setHoveredItem(null);
    }
  }, [sidebarExtended]);

  // 🔹 Handle click on main menu items
  const handleMenuClick = (itemId, e) => {
    if (itemId === 3 && hasCloudAssets) e.preventDefault();
    if (itemId === 7 && hasComplianceAssets) e.preventDefault();

    switch (itemId) {
      case 3:
        if (hasCloudAssets) {
          setActiveNav(3);
          setOpenCloudSubMenu((prev) => !prev);
          setOpenComplianceSubMenu(false);
        } else {
          setActiveNav(3);
          resetSubNav();
        }
        break;

      case 7:
        if (hasComplianceAssets) {
          setActiveNav(7);
          setOpenComplianceSubMenu((prev) => !prev);
          setOpenCloudSubMenu(false);
        } else {
          setActiveNav(7);
          resetSubNav();
        }
        break;

      default:
        setActiveNav(itemId);
        resetSubNav();
        break;
    }
  };

  // 🔹 Common styles
  const navItemStyle = (isActive) => ({
    backgroundColor: isActive ? theme.border : "",
    height: sidebarExtended ? "50px" : "60px",
    transition: "all 0.3s ease",
    cursor: "pointer",
    position: "relative",
  });

  const iconStyle = {
    color: theme.textColor,
    transition: "all 0.3s ease",
  };

  const labelStyle = (isActive) => ({
    color: theme.textColor,
    // fontSize: sidebarFontSize,
    marginLeft: sidebarExtended ? "0px" : "-240px",
    fontWeight: isActive ? "bold" : "normal",
    transition: "margin-left 0.3s ease",
  });

  // 🔹 Main Render
  return (
    <div
      className={`d-flex flex-column px-2 pb-3`}
      style={{
        height: height,
        width: sidebarWidthVal - (sidebarExtended ? 10 : 6),
        marginBottom: sidebarExtended ? 260 : 130,
        background: theme.background2,
        transition: "all 0.3s ease",
      }}
    >
      {Object.values(sidebarMap).map((item) => (
        <div
          key={item.id}
          onMouseEnter={(e) => handleMouseEnter(e, item.id)}
          onMouseLeave={() => !sidebarExtended && setHoveredItem(null)}
          style={{ position: "relative" }}
        >
          <Link
            to={item.path}
            onClick={(e) => handleMenuClick(item.id, e)}
            className={`nav-link text-white my-2 d-flex align-items-center rounded-3 sidebar-item ${
              activeNav === item.id ? "active" : ""
            }`}
            style={navItemStyle(activeNav === item.id)}
          >
            <i
              className={`${
                activeNav === item.id ? item.activeIcon : item.icon
              } ${sidebarExtended ? "fs-6" : "fs-4"} my-2 ps-1`}
              style={{
                ...iconStyle,
                margin: '0 20px',
              }}
            />
            <div style={labelStyle(activeNav === item.id)}>{item.title}</div>

            {/* ▼ Toggle Icon (Only for expandable items) */}
            {((item.id === 3 && hasCloudAssets) ||
              (item.id === 7 && hasComplianceAssets)) && (
              <span
                className="mx-3"
                style={{
                  fontSize: "12px",
                  color: theme.textColor,
                  transform:
                    (item.id === 3 && openCloudSubMenu) ||
                    (item.id === 7 && openComplianceSubMenu)
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              >
                ▼
              </span>
            )}
          </Link>

          {/* Cloud Submenu (expanded mode) */}
          {item.id === 3 &&
            openCloudSubMenu &&
            sidebarExtended &&
            hasCloudAssets && (
              <SubMenu
                items={item.subMenu}
                activeSubNav={activeCloudSubNav}
                setActiveSubNav={setActiveCloudSubNav}
                setActiveNav={() => setActiveNav(3)}
                resetOtherSubNav={() => setActiveComplianceSubNav(null)}
                theme={theme}
                // sidebarFontSize={sidebarFontSize}
              />
            )}

          {/* Compliance Submenu (expanded mode) */}
          {item.id === 7 &&
            openComplianceSubMenu &&
            sidebarExtended &&
            hasComplianceAssets && (
              <SubMenu
                items={item.subMenu}
                activeSubNav={activeComplianceSubNav}
                setActiveSubNav={setActiveComplianceSubNav}
                setActiveNav={() => setActiveNav(7)}
                resetOtherSubNav={() => setActiveCloudSubNav(null)}
                theme={theme}
                // sidebarFontSize={sidebarFontSize}
              />
            )}

          {/* 🌟 Hover Submenu (Collapsed Mode) */}
          {!sidebarExtended &&
            hoveredItem === item.id &&
            ((item.id === 3 && hasCloudAssets) ||
              (item.id === 7 && hasComplianceAssets)) && (
              <div
                className="shadow-lg rounded-4 py-2 pe-3"
                style={{
                  position: "fixed",
                  top: hoverPosition.top,
                  left: hoverPosition.left,
                  zIndex: 9999,
                  minWidth: 230,
                  background: theme.background2,
                  border: `1px solid ${theme.border}`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  animation: "fadeIn 0.15s ease-in-out",
                }}
              >
                <SubMenu
                  items={item.subMenu}
                  activeSubNav={
                    item.id === 3 ? activeCloudSubNav : activeComplianceSubNav
                  }
                  setActiveSubNav={
                    item.id === 3
                      ? setActiveCloudSubNav
                      : setActiveComplianceSubNav
                  }
                  setActiveNav={() => setActiveNav(item.id)}
                  resetOtherSubNav={() =>
                    item.id === 3
                      ? setActiveComplianceSubNav(null)
                      : setActiveCloudSubNav(null)
                  }
                  theme={theme}
                  // sidebarFontSize={sidebarFontSize}
                />
              </div>
            )}
        </div>
      ))}

      {/* ✨ Hover + fade animation */}
      <style>
        {`
          /* 🔹 Main Sidebar Items */
          .sidebar-item {
            position: relative;
            border-radius: 8px;
            transition: background 0.2s ease, color 0.2s ease;
          }

          .sidebar-item:hover {
            background: ${theme.border}66;
            color: ${theme.textColor};
          }

          .sidebar-item.active {
            background: ${theme.border};
          }

          /* 🔹 Icons and Text inside Sidebar */
          .sidebar-item i,
          .sidebar-item div {
            transition: color 0.2s ease;
          }

          .sidebar-item:hover i,
          .sidebar-item:hover div {
            color: ${theme.textColor};
          }

          /* 🔹 Submenu Items */
          .submenu-link {
            border-radius: 6px;
            transition: background 0.2s ease, transform 0.15s ease;
          }

          .submenu-link:hover {
            background: ${theme.border}66;
            transform: translateX(3px);
          }

          /* 🔹 Fade animation for hover popups */
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-4px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </div>
  );
};

// ♻️ Submenu Renderer with Hover
const SubMenu = ({
  items,
  activeSubNav,
  setActiveSubNav,
  setActiveNav,
  resetOtherSubNav,
  theme,
  sidebarFontSize,
}) => {
  const { 
    hasAwsAssets, 
    hasAzureAssets, 
    hasGcpAssets,
    hasOciAssets
  } = useNav();
  if (!items) return null;

  const getBadgeVisibility = (sub) => {
    if (sub.path?.toLowerCase().includes("aws")) return hasAwsAssets;
    if (sub.path?.toLowerCase().includes("azure")) return hasAzureAssets;
    if (sub.path?.toLowerCase().includes("gcp")) return hasGcpAssets;
    if (sub.path?.toLowerCase().includes("oci")) return hasOciAssets;
    return false;
  };

  return (
    <div className="ps-3">
      {Object.values(items).map((sub) => {
        const showBadge = getBadgeVisibility(sub);

        return (
          <Link
            key={sub.id}
            to={sub.path}
            onClick={() => {
              setActiveNav();
              setActiveSubNav(sub.id);
              resetOtherSubNav();
            }}
            className="nav-link text-white my-1 d-flex align-items-center rounded-3 submenu-link"
            style={{
              position: "relative",
              backgroundColor:
                activeSubNav === sub.id ? theme.border : "transparent",
              // fontSize: sidebarFontSize,
              padding: "10px 12px",
              height: "40px",
              whiteSpace: "nowrap",
              transition: "all 0.3s ease",
            }}
          >
            <i
              className={`${
                activeSubNav === sub.id ? sub.activeIcon : sub.icon
              } me-2`}
              style={{ color: theme.textColor }}
            />
            <span style={{ color: theme.textColor }}>{sub.title}</span>

            {Boolean(showBadge) && (
              <span
                className="position-absolute translate-middle p-1 bg-danger border border-light rounded-circle"
                style={{
                  width: "8px",
                  height: "8px",
                  transform: "translate(-50%, -30%)",
                }}
              >
                <span className="visually-hidden">New alerts</span>
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );
};

export default SidebarItems;
